import { currentUserQuery } from "@common/api/users"
import { workPackageTasksQuery } from "@common/api/work-packages"
import { useDeleteWorkTaskStepMutation } from "@common/api/work-task-steps"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { formatCount } from "@common/lib/number"
import { hasPermission } from "@common/lib/permissions"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"
import {
  flatSteps,
  stepTree,
} from "@/routes/_layout/work-orders/$workOrderId/-lib/steps"

export const Route = createFileRoute(
  "/_layout/work-orders/$workOrderId/tasks/$taskId/steps/$stepId/delete",
)({
  beforeLoad: async ({ context: { queryClient }, params, search }) => {
    const me = await queryClient.ensureQueryData(currentUserQuery())
    if (!hasPermission(me, "work_task:update")) {
      throw redirect({
        to: "/work-orders/$workOrderId/tasks/$taskId",
        params,
        search,
      })
    }
  },
  component: DeleteStepDialog,
})

function DeleteStepDialog(): React.ReactElement {
  const { workOrderId, taskId, stepId } = Route.useParams()
  const { data: workOrder } = useSuspenseQuery(
    workPackageTasksQuery({ id: workOrderId }),
  )
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const mutation = useDeleteWorkTaskStepMutation()
  const task = workOrder.tasks?.find(({ id }) => id === taskId)
  const node = flatSteps(stepTree(task?.steps ?? [])).find(
    ({ step }) => step.id === stepId,
  )
  const substeps = node ? flatSteps(node.children).length : 0
  const removed =
    substeps > 0
      ? ` and its ${formatCount(substeps)} sub-step${substeps === 1 ? "" : "s"}`
      : ""
  const description = node
    ? `“${node.step.description}”${removed} will be removed from the task.`
    : "This step will be removed from the task."

  const close = () =>
    navigate({
      to: "/work-orders/$workOrderId/tasks/$taskId",
      params: { workOrderId, taskId },
      search: true,
    })

  const confirm = () =>
    mutation.mutate(
      { work_package_id: workOrderId, work_task_id: taskId, step_id: stepId },
      {
        onSuccess: () => {
          showToast("Archived", "Step archived.", "success")
          close()
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <ConfirmDialog
      title="Archive this step?"
      description={description}
      action="Archive"
      tone="default"
      pendingAction="Archiving…"
      pending={mutation.isPending}
      onConfirm={confirm}
      onCancel={close}
    />
  )
}
